import { Box } from '@spark-web/box';
import { Columns } from '@spark-web/columns';
import { Text } from '@spark-web/text';
import { DataListItem } from './types';

// Leading trims are off so each row's padding is measured from the full line box; with trims on the
// text box hugs the cap height and a wrapped value's descenders would sit against the row's border.
const ROW_PADDING = { paddingX: 'small', paddingY: 'xsmall', borderBottom: 'standard' } as const;

function Label({ symbol, label }: Pick<DataListItem, 'symbol' | 'label'>) {
  return (
    <Text size="standard" tone="muted" baseline={false}>
      <>
        {label}
        {symbol ? <sup>{symbol}</sup> : null}
      </>
    </Text>
  );
}

function Row({ symbol, label, value, valueOverflowStrategy = 'breakword', description }: DataListItem) {
  // Not every item is a term/definition pair. Callers also pass standalone statements with no value —
  // "No early repayment fees", "First repayment due 90 days after installation", vendor-authored
  // product descriptions, and the disclaimer paragraphs the completed finance summary routes through
  // here. Those get the full row width (and no dt/dd, since there is no term being defined); a rigid
  // 50/50 split would wrap a 600-character disclaimer into half the container with an empty cell
  // beside it.
  if (!value && !description) {
    return (
      <Box {...ROW_PADDING}>
        <Label symbol={symbol} label={label} />
      </Box>
    );
  }

  return (
    <Columns template={[1, 1]} gap="large" {...ROW_PADDING}>
      <Box as="dt">
        <Label symbol={symbol} label={label} />
      </Box>
      <Box as="dd">
        {value ? (
          <Text size="standard" overflowStrategy={valueOverflowStrategy} baseline={false}>
            {value}
          </Text>
        ) : null}
        {description ? (
          <Text size="small" tone="muted" baseline={false}>
            ({description})
          </Text>
        ) : null}
      </Box>
    </Columns>
  );
}

// An eyebrow label starting a sub-group of rows. It's a term with no definition, so the group still
// renders an empty <dd> to keep the list structure valid, and carries no border of its own — the row
// beneath it draws that. Space above separates it from the preceding group, unless it leads the list
// and already sits below the heading.
function CaptionRow({ label, leadsList }: { label: DataListItem['label']; leadsList: boolean }) {
  return (
    <Columns template={[1, 1]} gap="large" paddingX="small" paddingTop={leadsList ? undefined : 'small'}>
      <Box as="dt">
        <Text size="xsmall" tone="muted" transform="uppercase" weight="semibold">
          {label}
        </Text>
      </Box>
      <Box as="dd" />
    </Columns>
  );
}

interface Props {
  title?: string;
  items?: DataListItem[];
}

function DataList({ title, items }: Props) {
  return (
    <Box>
      {title ? (
        <Box paddingX="small" paddingBottom="small" borderBottom="primary">
          <Text as="h4" size="small" weight="semibold" transform="uppercase" tone="primaryActive">
            {title}
          </Text>
        </Box>
      ) : null}
      <Box as="dl" paddingTop={title ? 'medium' : undefined}>
        {items?.map((item, index) =>
          item.caption ? (
            <CaptionRow key={`${item.label}-${index}`} label={item.label} leadsList={index === 0} />
          ) : (
            <Row key={`${item.label}-${index}`} {...item} />
          ),
        )}
      </Box>
    </Box>
  );
}

export default DataList;
